import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9221f1Api, F921 } from '../../../services/pw9221f1Api';

export type Mode = 'browse' | 'add' | 'edit';

const STATUS_MAP: Record<string, string> = {
  '': '尚未處理',
  'V': '已初核,未審核',
  'P': '已審核,未複核',
  'Y': '已核准',
  'N': '不准假',
};

export function usePW9221F1Form() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F921[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [empID, setEmpID] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [totalLeave, setTotalLeave] = useState(0);
  const [unit, setUnit] = useState('');
  const [reason, setReason] = useState('');
  const [agent, setAgent] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [approveLevel, setApproveLevel] = useState('');
  const [approveReason, setApproveReason] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';
  const isApproved = current?.f921_30 === 'Y' || current?.f921_30 === 'N';

  const getStatusLabel = (code: string) => STATUS_MAP[code] || '未知';

  // Load all records
  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await pw9221f1Api.list(1, 500, keyword);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setEmpID(current.f921_02);
      setLeaveType(current.f921_12);
      setStartDate(current.f921_13?.substring(0, 10) || '');
      setStartTime(current.f921_14);
      setEndDate(current.f921_15?.substring(0, 10) || '');
      setEndTime(current.f921_16);
      setTotalLeave(current.f921_19);
      setUnit(current.f921_18);
      setReason(current.f921_21);
      setAgent(current.f921_10);
      setDeptCode(current.f921_31);
      setApproveLevel(current.f921_29);
      setApproveReason(current.f921_27);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setEmpID('');
    setLeaveType('');
    const today = new Date().toISOString().substring(0, 10);
    setStartDate(today);
    setStartTime('');
    setEndDate(today);
    setEndTime('');
    setTotalLeave(0);
    setUnit('');
    setReason('');
    setAgent('');
    setDeptCode('');
    setApproveLevel('');
    setApproveReason('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current || isApproved) return;
    setMode('edit');
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) {
      setEmpID(current.f921_02);
      setLeaveType(current.f921_12);
      setStartDate(current.f921_13?.substring(0, 10) || '');
      setStartTime(current.f921_14);
      setEndDate(current.f921_15?.substring(0, 10) || '');
      setEndTime(current.f921_16);
      setTotalLeave(current.f921_19);
      setUnit(current.f921_18);
      setReason(current.f921_21);
      setAgent(current.f921_10);
      setDeptCode(current.f921_31);
      setApproveLevel(current.f921_29);
      setApproveReason(current.f921_27);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await pw9221f1Api.create({
          f921_02: empID, f921_10: agent, f921_11: new Date().toISOString().substring(0, 10),
          f921_12: leaveType, f921_13: startDate, f921_14: startTime,
          f921_15: endDate, f921_16: endTime, f921_18: unit,
          f921_19: totalLeave, f921_21: reason, f921_27: approveReason,
          f921_29: approveLevel, f921_31: deptCode, details: [],
        });
        message.success('新增成功');
      } else {
        await pw9221f1Api.update(current!.f921_01, {
          f921_10: agent, f921_12: leaveType,
          f921_13: startDate, f921_14: startTime,
          f921_15: endDate, f921_16: endTime, f921_18: unit,
          f921_19: totalLeave, f921_21: reason, f921_27: approveReason,
          f921_29: approveLevel, details: [],
        });
        message.success('修改成功');
      }
      const data = await loadRecords();
      if (mode === 'add') {
        setCurrentIdx(0); // newest first
      } else {
        const idx = data.findIndex((r: F921) => r.f921_01 === current!.f921_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await pw9221f1Api.delete(current.f921_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch (err: any) {
      message.error(err?.response?.data?.error || '刪除失敗');
    }
  };

  // Quick search
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    loadRecords(searchText).then(data => {
      if (data.length > 0) setCurrentIdx(0);
      else setCurrentIdx(-1);
    });
  };

  return {
    mode, isEditing, isApproved, loading,
    records, current, currentIdx,
    empID, setEmpID,
    leaveType, setLeaveType,
    startDate, setStartDate,
    startTime, setStartTime,
    endDate, setEndDate,
    endTime, setEndTime,
    totalLeave, setTotalLeave,
    unit, setUnit,
    reason, setReason,
    agent, setAgent,
    deptCode, setDeptCode,
    approveLevel, setApproveLevel,
    approveReason, setApproveReason,
    getStatusLabel,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  };
}
